import { AdminUser } from '@models/auth';
import { PagenatedResponse, SearchPagenateQuery } from '@models/common';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { createSafeStorage, createSizePartialize } from '@utils/localStorage';

const PAGE_SIZE = 20;

interface AdminUsersContainer {
  contents: AdminUser[];
  page: number;
  size: number;
  total: number;
  hasNext: boolean;
}

interface AdminUsersStoreProps {
  searchResultContainer: AdminUsersContainer;
  searchQuery: SearchPagenateQuery;
  searchKeyword?: string;
  setStartDate: (date: string | null) => void;
  setEndDate: (date: string | null) => void;
  setSearchKeyword: (keyword: string) => void;
  updateSearchQuery: (query: { [key: string]: any }) => void;
  resetSearchQuery: (pageSize?: number) => void;
  setAdminUsersContainer: (result: PagenatedResponse<AdminUser>) => void;
}

export const useAdminUsersStore = create(
  persist<AdminUsersStoreProps>(
    (set) => ({
      searchResultContainer: {
        contents: [] as AdminUser[],
        total: 0,
        page: 0,
        size: 0,
        hasNext: false,
      },
      searchQuery: {
        page: 0,
        size: PAGE_SIZE,
        type: 'all',
        startDate: undefined,
        endDate: undefined,
        partnerFilter: 'all',
      },
      setStartDate: (date: string | null) => {
        set((state) => {
          return { ...state, searchQuery: { ...state.searchQuery, startDate: date } };
        });
      },
      setEndDate: (date: string | null) => {
        set((state) => {
          return { ...state, searchQuery: { ...state.searchQuery, endDate: date } };
        });
      },
      searchKeyword: undefined,
      setAdminUsersContainer: (result) => {
        set((state) => {
          // 메모리 효율성을 위해 실제 데이터만 저장
          const newValue = {
            ...state.searchResultContainer,
            ...result,
            contents: result?.contents || [],
          };
          return { ...state, searchResultContainer: newValue };
        });
      },
      setSearchKeyword: (keyword: string) => {
        set((state) => {
          return {
            ...state,
            searchKeyword: keyword,
            searchResultContainer: { ...state.searchResultContainer, size: 0 },
          };
        });
      },
      updateSearchQuery: (query: { [key: string]: any }) => {
        set((state) => {
          return {
            ...state,
            searchQuery: { ...state.searchQuery, ...query },
            searchResultContainer: { ...state.searchResultContainer, size: 0 },
          };
        });
      },
      resetSearchQuery: (pageSize?: number) => {
        set({
          searchQuery: {
            page: 0,
            size: pageSize || PAGE_SIZE,
            type: 'all',
            startDate: undefined,
            endDate: undefined,
            childBirthdayStart: undefined,
            childBirthdayEnd: undefined,
            partnerFilter: 'all',
          },
        });
      },
    }),
    {
      name: 'igogo-admin-users',
      storage: createSafeStorage(),
      partialize: createSizePartialize<AdminUsersStoreProps>(100),
    },
  ),
);
